import { FC } from 'react';
import { Helmet } from 'react-helmet';
import { AuthContextProvider, urls } from '@getstream/ui-library/build';

import Layout from './components/Shared/Layout';
import StepController from './components/Shared/StepController/';
import GetStarted from './components/Shared/GetStarted';

import { useGetStartedContext } from './components/Shared/GetStarted/GetStarted.context';

import VideoController from './components/Video/VideoController';

import Step1 from './components/Video/Steps/Step-1';
import Step2 from './components/Video/Steps/Step-2';
import Step3 from './components/Video/Steps/Step-3';
import Step4 from './components/Video/Steps/Step-4';
import Step5 from './components/Video/Steps/Step-5';
import Step6 from './components/Video/Steps/Step-6';
import Step7 from './components/Video/Steps/Step-7';
import Step8 from './components/Video/Steps/Step-8';

import { Content, Product } from './types/tour';

import { useLogs } from './hooks/useLogs';

import { sidebar } from './data/video/sidebar';
import { content } from './data/video/content';

import introImage from './images/video/introduction.png';

import '@stream-io/video-react-sdk/dist/css/styles.css';
import '@getstream/ui-library/build/assets/css/main.css';
import './App.css';
import './styles/video.css';

export type VideoGetStartedProps = {
  content: Content[];
  image: string;
};

const VideoGetStarted: FC<VideoGetStartedProps> = ({ image, content }) => {
  const { state, goPrev, goNext, setComplete } = useGetStartedContext();
  const { steps, first, last, current, showPrev, showNext } = state;

  const Step = [Step1, Step2, Step3, Step4, Step5, Step6, Step7, Step8][
    current
  ];

  if (current === first || current === last) {
    return (
      <Step
        setComplete={() => setComplete(current, current)}
        goNext={goNext}
        content={content[current]}
        image={image}
      />
    );
  }

  return (
    <VideoController>
      <Layout product={Product.Video}>
        <Step
          setComplete={() => setComplete(current, current)}
          goNext={goNext}
          content={content[current]}
        />
        <StepController
          {...{
            goPrev,
            showPrev,
            showNext,
            goNext,
            disableNext: !steps[current].completed,
          }}
        />
      </Layout>
    </VideoController>
  );
};

const Video = () => {
  useLogs();

  return (
    <>
      <Helmet>
        <title>Video API Tutorial</title>
        <meta name="description" content="Try Stream Chat for Free" />
        <meta property="og:title" content="Chat for your application" />
        <meta
          property="og:description"
          content="Stream provides an easy to use API, React components and UI kit for Chat"
        />
        <meta property="og:url" content="/chat/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary"></meta>
        <meta name="twitter:site" content="@getstream_io"></meta>
        <link rel="canonical" href="https://getstream.io/video/" />
        <link
          rel="icon"
          href={`${import.meta.env.BASE_URL}/favicon-16x16.png`}
          sizes="16x16"
        />
        <link
          rel="icon"
          href={`${import.meta.env.BASE_URL}/favicon-32x32.png`}
          sizes="32x32"
        />
        <link
          rel="icon"
          href={`${import.meta.env.BASE_URL}/favicon-96.png`}
          sizes="96x96"
        />
        <link
          rel="stylesheet"
          href="https://cdn.jsdelivr.net/npm/stream-chat-react@7.0.0/dist/css/index.css"
        />
        <script
          src="https://accounts.google.com/gsi/client"
          async
          defer
        ></script>
      </Helmet>
      <AuthContextProvider apiBaseURL={import.meta.env.VITE_API_BASEURL}>
        <GetStarted
          steps={sidebar}
          title="Video & Audio API tour"
          leaveTourLink={urls.video.root}
          current={0}
        >
          <VideoGetStarted image={introImage} content={content} />
        </GetStarted>
      </AuthContextProvider>
    </>
  );
};

export default Video;
